import * as THREE from 'three';
import { Representation } from 'forma-embedded-view-sdk/dist/internal/representations';
import { Clipper, EndType, JoinType, Paths64 } from 'clipper2-ts';
import { WorkerOutput } from '@/workers/sunlight.worker';

export interface WallPolygon {
	vertices: THREE.Vector3[];
	normal: THREE.Vector3;
	buildingPath: string;
}

function offsetPolygonOutward(vertices: THREE.Vector2[], offset: number): THREE.Vector2[] {
	if (vertices.length < 3) return vertices;

	const SCALE = 1000; // Scale factor for precision (clipper2 uses integers)

	const path = vertices.map((v) => ({
		x: Math.round(v.x * SCALE),
		y: Math.round(v.y * SCALE),
	}));

	const solution: Paths64 = Clipper.inflatePaths(
		[path],
		offset * SCALE,
		JoinType.Miter,
		EndType.Polygon
	);

	if (!solution || solution.length === 0 || solution[0].length < 3) {
		console.warn('Clipper offset resulted in empty polygon, returning original');
		return vertices;
	}

	const offsetVertices = solution[0].map(
		(p) => new THREE.Vector2(Number(p.x) / SCALE, Number(p.y) / SCALE)
	);

	return offsetVertices;
}

/**
 * Extracts vertical wall surfaces from building exterior outline
 * Traces exterior edges by finding surfaces that appear only once across all spaces
 */
export function extractExteriorWallsFromRooms(
	buildingObject: THREE.Object3D,
	buildingGraph: Representation.GraphBuilding
): WallPolygon[] {
	const wallPolygons: WallPolygon[] = [];
	const outwardOffset = 0.05; // 5cm offset for facade analysis

	// Calculate alignment offset between building mesh and graph coordinates
	const buildingBounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, minZ: Infinity };
	const pointsBounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };

	if (buildingObject instanceof THREE.Mesh && buildingObject.geometry) {
		const posAttr = buildingObject.geometry.getAttribute('position');
		for (let i = 0; i < posAttr.count; i++) {
			buildingBounds.minX = Math.min(buildingBounds.minX, posAttr.getX(i));
			buildingBounds.maxX = Math.max(buildingBounds.maxX, posAttr.getX(i));
			buildingBounds.minY = Math.min(buildingBounds.minY, posAttr.getY(i));
			buildingBounds.maxY = Math.max(buildingBounds.maxY, posAttr.getY(i));
			buildingBounds.minZ = Math.min(buildingBounds.minZ, posAttr.getZ(i));
		}
	}

	for (const level of buildingGraph.data.levels) {
		if (!level.points) continue;
		for (const pid in level.points) {
			const [x, y] = level.points[pid] ?? [0, 0];
			pointsBounds.minX = Math.min(pointsBounds.minX, x);
			pointsBounds.maxX = Math.max(pointsBounds.maxX, x);
			pointsBounds.minY = Math.min(pointsBounds.minY, y);
			pointsBounds.maxY = Math.max(pointsBounds.maxY, y);
		}
	}

	const offsetX = (buildingBounds.minX + buildingBounds.maxX) / 2 - (pointsBounds.minX + pointsBounds.maxX) / 2;
	const offsetY = (buildingBounds.minY + buildingBounds.maxY) / 2 - (pointsBounds.minY + pointsBounds.maxY) / 2;

	// Process each floor level
	for (let floor = 0; floor < buildingGraph.data.levels.length; floor++) {
		const level = buildingGraph.data.levels[floor];
		const { points: pointsObj, surfaces, spaces } = level;
		if (!pointsObj || !surfaces || !spaces) continue;

		const floorZ = buildingBounds.minZ + floor * level.height;

		// Find exterior edges (edges that appear only once across all spaces)
		const exteriorEdges = new Map<string, { pointA: string; pointB: string }>();
		
		for (const space of spaces) {
			if (!space.outerLoop || !Array.isArray(space.outerLoop)) continue;
			
			for (const loopItem of space.outerLoop) {
				const surface = surfaces.find((s) => s.id === loopItem.surfaceId);
				if (!surface) continue;
				
				const p1 = surface.pointA;
				const p2 = surface.pointB;
				const edgeKey = p1 < p2 ? `${p1}-${p2}` : `${p2}-${p1}`;
				
				if (exteriorEdges.has(edgeKey)) {
					exteriorEdges.delete(edgeKey);
				} else {
					exteriorEdges.set(edgeKey, { pointA: p1, pointB: p2 });
				}
			}
		}
		
		// Build outline by following exterior edges in order
		const outlineEdges = Array.from(exteriorEdges.values());
		if (outlineEdges.length < 3) continue;
		
		const levelPoints: THREE.Vector2[] = [];
		{
			let currentPoint = outlineEdges[0].pointA;
			levelPoints.push(new THREE.Vector2(...(pointsObj[currentPoint] || [0, 0])));
			
			const used = new Set<string>([`${outlineEdges[0].pointA}-${outlineEdges[0].pointB}`]);
			currentPoint = outlineEdges[0].pointB;
			
			while (used.size < outlineEdges.length) {
				levelPoints.push(new THREE.Vector2(...(pointsObj[currentPoint] || [0, 0])));
				
				// Find next connected edge
				let found = false;
				for (const edge of outlineEdges) {
					const key = `${edge.pointA}-${edge.pointB}`;
					if (used.has(key)) continue;
					
					if (edge.pointA === currentPoint) {
						used.add(key);
						currentPoint = edge.pointB;
						found = true;
						break;
					} else if (edge.pointB === currentPoint) {
						used.add(key);
						currentPoint = edge.pointA;
						found = true;
						break;
					}
				}
				
				if (!found) break;
			}
		}

		if (levelPoints.length < 3) continue;

		const expandedVertices = offsetPolygonOutward(levelPoints, outwardOffset);
		if (expandedVertices.length < 3) {
			console.warn(`Outward offset failed for floor ${floor}`);
			continue;
		}

		const centerX = levelPoints.reduce((sum, v) => sum + v.x, 0) / levelPoints.length + offsetX;
		const centerY = levelPoints.reduce((sum, v) => sum + v.y, 0) / levelPoints.length + offsetY;
		const levelCenter = new THREE.Vector3(centerX, centerY, floorZ + level.height / 2);

		// Create one quad polygon per vertical wall segment.
		// Using a single face here avoids double-sampling the same facade area,
		// which previously happened when each wall was split into two triangles.
		for (let i = 0; i < expandedVertices.length; i++) {
			const nextI = (i + 1) % expandedVertices.length;
			const v1 = expandedVertices[i];
			const v2 = expandedVertices[nextI];

			const bottomLeft = new THREE.Vector3(v1.x + offsetX, v1.y + offsetY, floorZ);
			const bottomRight = new THREE.Vector3(v2.x + offsetX, v2.y + offsetY, floorZ);
			const topLeft = new THREE.Vector3(
				v1.x + offsetX,
				v1.y + offsetY,
				floorZ + level.height
			);
			const topRight = new THREE.Vector3(
				v2.x + offsetX,
				v2.y + offsetY,
				floorZ + level.height
			);

			// Calculate normal (pointing outward from level center)
			const edge = new THREE.Vector3().subVectors(bottomRight, bottomLeft);
			const wallNormal = new THREE.Vector3(-edge.y, edge.x, 0).normalize();

			// Verify normal points outward from level center
			const wallCenter = bottomLeft.clone().add(topRight).multiplyScalar(0.5);
			const outwardDir = new THREE.Vector3().subVectors(wallCenter, levelCenter);
			outwardDir.z = 0; // Keep only horizontal component

			if (outwardDir.lengthSq() > 0.01) {
				outwardDir.normalize();
				// If normal points inward (opposite to outward direction), flip it
				if (wallNormal.dot(outwardDir) < 0) {
					wallNormal.negate();
				}
			}

			wallPolygons.push({
				vertices: [bottomLeft, bottomRight, topRight, topLeft],
				normal: wallNormal.clone(),
				buildingPath: `level-${floor}`,
			});
		}
	}

	return wallPolygons;
}

/**
 * Generates grid points on wall surfaces for sunlight analysis
 * Accepts pre-extracted wall polygons from extractExteriorWallsFromRooms
 */
export function generateFacadeGridPoints(
	wallPolygons: WallPolygon[],
	gridSpacing: number = 1.0
): Array<{ x: number; y: number; z: number; normal: { x: number; y: number; z: number }; wallId: string }> {
	const allGridPoints: Array<{
		x: number;
		y: number;
		z: number;
		normal: { x: number; y: number; z: number };
		wallId: string;
	}> = [];

	wallPolygons.forEach((polygon, wallIndex) => {
		const { vertices, normal } = polygon;
		// Generate unique wallId based on index and normal direction
		const wallId = `wall_${wallIndex}_${normal.x.toFixed(2)}_${normal.y.toFixed(2)}_${normal.z.toFixed(2)}`;
		
		// Calculate bounding box
		const bbox = new THREE.Box3().setFromPoints(vertices);
		const center = bbox.getCenter(new THREE.Vector3());

		// Create local 2D coordinate system on the wall
		const worldUp = new THREE.Vector3(0, 0, 1);
		const right = new THREE.Vector3().crossVectors(worldUp, normal).normalize();

		if (right.lengthSq() < 0.01) {
			right.set(1, 0, 0);
		}

		const actualUp = new THREE.Vector3().crossVectors(normal, right).normalize();
		if (actualUp.z < 0) {
			actualUp.negate();
			right.negate();
		}

		// Project vertices to 2D
		const vertices2D = vertices.map((v) => {
			const relative = new THREE.Vector3().subVectors(v, center);
			return new THREE.Vector2(relative.dot(right), relative.dot(actualUp));
		});

		// Get 2D bounding box
		const minU = Math.min(...vertices2D.map((v) => v.x));
		const maxU = Math.max(...vertices2D.map((v) => v.x));
		const minV = Math.min(...vertices2D.map((v) => v.y));
		const maxV = Math.max(...vertices2D.map((v) => v.y));

		const width = maxU - minU;
		const height = maxV - minV;

		// Calculate grid cells
		const cellsX = Math.max(1, Math.ceil(width / gridSpacing));
		const cellsY = Math.max(1, Math.ceil(height / gridSpacing));
		const cellWidth = width / cellsX;
		const cellHeight = height / cellsY;

		// Generate grid points at cell centers
		for (let x = 0; x < cellsX; x++) {
			for (let y = 0; y < cellsY; y++) {
				const u = minU + (x + 0.5) * cellWidth;
				const v = minV + (y + 0.5) * cellHeight;
				const point2D = new THREE.Vector2(u, v);

				// Check if point is inside the wall polygon.
				if (isPointInPolygon2D(point2D, vertices2D)) {
					// Convert back to 3D world coordinates
					const point3D = center
						.clone()
						.add(right.clone().multiplyScalar(u))
						.add(actualUp.clone().multiplyScalar(v));

					allGridPoints.push({
						x: point3D.x,
						y: point3D.y,
						z: point3D.z,
						normal: {
							x: normal.x,
							y: normal.y,
							z: normal.z,
						},
						wallId,
					});
				}
			}
		}
	});

	return allGridPoints;
}

/**
 * Point-in-polygon test for convex/concave wall faces in 2D local space.
 */
function isPointInPolygon2D(
	point: THREE.Vector2,
	polygon: THREE.Vector2[]
): boolean {
	if (polygon.length < 3) {
		return false;
	}

	let inside = false;
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const xi = polygon[i].x;
		const yi = polygon[i].y;
		const xj = polygon[j].x;
		const yj = polygon[j].y;

		const intersects =
			(yi > point.y) !== (yj > point.y) &&
			point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || Number.EPSILON) + xi;

		if (intersects) {
			inside = !inside;
		}
	}

	return inside;
}

/**
 * Applies colors to facade instances based on sunlight hours
 * Similar to changeSunlightVisualizationMetric for daily sunlight
 */
function applyFacadeColors(
	instancedMesh: THREE.InstancedMesh,
	gridPointsData: Array<{
		point: THREE.Vector3;
		averageDailySunlightHours: number;
		pointIndex: number;
		roomName: string;
	}>,
	maxHours: number
): void {
	const color = new THREE.Color();

	gridPointsData.forEach((data, index) => {
		if (index >= instancedMesh.count) return;

		const value = data.averageDailySunlightHours;
		const ratio = Math.min(Math.max(value / maxHours, 0), 1.0);

		// Color gradient: Red (bad/low) → Yellow (medium) → Green (good/high)
		if (ratio < 0.5) {
			color.setRGB(1, ratio * 2, 0);
		} else {
			color.setRGB(2 - ratio * 2, 1, 0);
		}

		instancedMesh.setColorAt(index, color);
	});

	if (instancedMesh.instanceColor) {
		instancedMesh.instanceColor.needsUpdate = true;
	}
}

/**
 * Visualizes facade sunlight analysis results using square grid
 */
export function visualizeFacadeSunlight(
	scene: THREE.Scene,
	gridPoints: Array<{
		x: number;
		y: number;
		z: number;
		normal?: { x: number; y: number; z: number };
		wallId?: string;
	}>,
	sunlightHours: (number | null)[],
	pointResults?: Array<WorkerOutput['results'][number] | null>,
	maxHours: number = 8,
	cellSize: number = 1.0
): void {
	// Clear any existing facade sunlight visualization
	clearFacadeSunlightVisualization(scene);

	if (gridPoints.length === 0) {
		console.warn('No grid points to visualize');
		return;
	}

	const facadeGroup = new THREE.Group();
	facadeGroup.name = 'facadeSunlightVisualization';

	// Create shared plane geometry for all grid squares
	const sharedGeometry = new THREE.PlaneGeometry(cellSize, cellSize);

	const sharedMaterial = new THREE.MeshBasicMaterial({
		side: THREE.DoubleSide,
		transparent: true,
		opacity: 1.0,
	});

	// Create instanced mesh for efficient rendering
	const instancedMesh = new THREE.InstancedMesh(
		sharedGeometry,
		sharedMaterial,
		gridPoints.length
	);
	instancedMesh.name = 'FacadeSunlightGridInstances';

	const matrix = new THREE.Matrix4();
	const color = new THREE.Color();
	const position = new THREE.Vector3();

	let instanceIndex = 0;

	// Create instances only for points WITH data (same as daily sunlight)
	gridPoints.forEach((point, index) => {
		const hours = sunlightHours[index];

		// Skip points without data
		if (hours === null || hours === undefined) {
			return;
		}

		// Set position at wall surface
		position.set(point.x, point.y, point.z);

		// Orient the square to face along the wall normal
		const faceNormal = point.normal 
			? new THREE.Vector3(point.normal.x, point.normal.y, point.normal.z).normalize()
			: new THREE.Vector3(0, 0, 1);
		
		const zUp = new THREE.Vector3(0, 0, 1);
		const tangent = new THREE.Vector3().crossVectors(zUp, faceNormal);

		if (tangent.lengthSq() < 1e-6) {
			tangent.set(1, 0, 0);
		}
		tangent.normalize();

		const bitangent = new THREE.Vector3().crossVectors(faceNormal, tangent).normalize();

		matrix.makeBasis(tangent, bitangent, faceNormal);
		matrix.setPosition(position);

		// Set instance transform and color at sequential index
		instancedMesh.setMatrixAt(instanceIndex, matrix);
		instancedMesh.setColorAt(instanceIndex, color);
		instanceIndex++;
	});

	// Set count to number of instances created
	instancedMesh.count = instanceIndex;

	// Update instance attributes
	instancedMesh.instanceMatrix.needsUpdate = true;
	if (instancedMesh.instanceColor) {
		instancedMesh.instanceColor.needsUpdate = true;
	}

	instancedMesh.computeBoundingSphere();

	// Store grid point data for tooltip display (sequential mapping like daily sunlight)
	const gridPointsData: Array<{
		point: THREE.Vector3;
		averageDailySunlightHours: number;
		pointIndex: number;
		roomName: string;
		wallId: string;
		normal: { x: number; y: number; z: number };
	}> = [];
	
	let dataIndex = 0;
	gridPoints.forEach((point, index) => {
		const hours = sunlightHours[index];
		if (hours !== null && hours !== undefined) {
			const resultDetails = pointResults?.[index] ?? null;
			gridPointsData[dataIndex] = {
				point: new THREE.Vector3(point.x, point.y, point.z),
				averageDailySunlightHours: hours,
				pointIndex: index,
				roomName: 'Facade',
				wallId: point.wallId || 'unknown',
				normal: point.normal || { x: 0, y: 0, z: 1 },
				...(resultDetails ?? {}),
			};
			dataIndex++;
		}
	});
	
	instancedMesh.userData.gridPointsData = gridPointsData;
	instancedMesh.userData.isFacadeSunlightVisualization = true;

	facadeGroup.add(instancedMesh);
	scene.add(facadeGroup);
	
	// Apply colors based on sunlight hours (like changeSunlightVisualizationMetric)
	applyFacadeColors(instancedMesh, gridPointsData, maxHours);
}

/**
 * Updates facade sunlight visualization progressively (for progressive rendering during calculation)
 */
export function updateFacadeSunlightVisualization(
	scene: THREE.Scene,
	gridPoints: Array<{
		x: number;
		y: number;
		z: number;
		normal?: { x: number; y: number; z: number };
	}>,
	sunlightHours: (number | null)[],
	pointResults?: Array<WorkerOutput['results'][number] | null>,
	maxHours: number = 8,
	cellSize: number = 1.0
): void {
	// Always recreate visualization from scratch (like daily sunlight does)
	visualizeFacadeSunlight(scene, gridPoints, sunlightHours, pointResults, maxHours, cellSize);
}

/**
 * Clears facade sunlight visualization
 */
export function clearFacadeSunlightVisualization(scene: THREE.Scene): void {
	const existingGroup = scene.getObjectByName('facadeSunlightVisualization');
	if (existingGroup) {
		existingGroup.traverse((child) => {
			if (child instanceof THREE.Mesh || child instanceof THREE.InstancedMesh) {
				child.geometry.dispose();
				if (child.material instanceof THREE.Material) {
					child.material.dispose();
				}
			}
		});
		scene.remove(existingGroup);
	}
}
